import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Menu } from '@mui/material';
import { ApplicantStages } from 'Components/Applicants/ApplicantsConstants';
import { IStagesToggler, JobStages, Stage } from 'Components/Applicants/ApplicantsModel';
import { useNotification } from 'Components/Notification';
import { useAppDispatch } from 'helpers/hooks';
import { MouseEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShButton } from 'shared/SharedStyles/ShInputs';
import { StyledMenuItem } from 'shared/SharedStyles/ShNavigation';
import { ApiStates } from 'shared/constants';
import { resetStageChange } from 'store/slices/employer/applicants/applicants-list-slice';
import { openInterviewDialog } from 'store/slices/employer/interviews/interviews-shared-slice';

export const StagesToggler = ({ applicant, changeStage, className }: IStagesToggler & Partial<HTMLElement>) => {
    const notification = useNotification();
    const dispatch = useAppDispatch();
    const { jobId } = useParams();
    /*
        Using map with Applicant id as key and element ref as value since the menu's ref is present in multiple locations opening one
        menu toggles multiple menus to open. With the help of this map toggling menu for appropriate element is possible.
    */
    const [stagesAnchorEl, setStagesAnchorEl] = useState<{ [key: number]: HTMLElement | null }>({ 0: null });

    const openStagesMenu = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>, indx: number) => {
        e.stopPropagation();
        setStagesAnchorEl({ ...stagesAnchorEl, [indx]: e.currentTarget });
    };

    const chngStg = (e: MouseEvent<HTMLLIElement, globalThis.MouseEvent>, stg: Lowercase<Stage>) => {
        e.stopPropagation();
        setStagesAnchorEl({ ...stagesAnchorEl, [applicant?.candidateEmployerJobId]: null });
        changeStage(stg, applicant?.candidateEmployerJobId);
        if (stg === 'interview' || stg === 'second_interview') {
            dispatch(openInterviewDialog({
                isInterviewDialogOpen: true,
                applicantInfo: applicant,
                candidateName: undefined,
                interviewId: undefined,
                jobId: parseInt(jobId ?? '')
            }))
        }
    };

    // Transforms stage code into appropriate stage label to be displayed in the button.
    const getTransformedLabel = (stage: Stage): string => {
        return stage?.split('_').map(str => str[0].toUpperCase() + str.substring(1, str.length).toLowerCase()).join(' ');
    };

    // Show snackbar notifications when the stage change is either successful or failed.
    useEffect(() => {
        if (applicant?.changeStateStatus === ApiStates.failed || applicant?.changeStateStatus === ApiStates.success) {
            notification.displayNotification({
                open: true,
                type: applicant?.changeStateStatus === ApiStates.failed ? 'error' : 'success',
                message: applicant?.changeStateResponse ?? ''
            });
            dispatch(resetStageChange(applicant?.candidateEmployerJobId));
        }
    }, [applicant?.candidateEmployerJobId, applicant?.changeStateResponse, applicant?.changeStateStatus, dispatch, notification]);

    return (<>
        <ShButton className={className} size='small' aria-label={getTransformedLabel(applicant?.jobStage)} variant="outlined"
            disabled={applicant?.changeStateStatus === ApiStates.pending}
            color={applicant?.jobStage?.toUpperCase() === JobStages.Interview ? 'primary' :
                (applicant?.jobStage?.toUpperCase() === JobStages.Hired ||
                    applicant?.jobStage?.toUpperCase() === JobStages.Offer) ? 'success' :
                    applicant?.jobStage?.toUpperCase() === JobStages.Rejected ? 'error' :
                        applicant?.jobStage?.toUpperCase() === JobStages.Phone_Screening ? 'secondary' : 'warning'}
            endIcon={<ExpandMoreIcon />} onClick={e => { openStagesMenu(e, applicant?.candidateEmployerJobId) }}>
            {applicant?.changeStateStatus === ApiStates.pending ? 'Updating Stage...' : getTransformedLabel(applicant?.jobStage)}
        </ShButton>
        {stagesAnchorEl[applicant?.candidateEmployerJobId] && <Menu id={'stage_menu' + applicant?.candidateEmployerJobId}
            aria-label={applicant?.jobStage} anchorEl={stagesAnchorEl[applicant?.candidateEmployerJobId]}
            open={stagesAnchorEl[applicant?.candidateEmployerJobId] !== null}
            onClose={() => setStagesAnchorEl({ ...stagesAnchorEl, [applicant?.candidateEmployerJobId]: null })}
            onClick={e => { setStagesAnchorEl({ ...stagesAnchorEl, [applicant?.candidateEmployerJobId]: null }); e.stopPropagation() }}>
            {ApplicantStages.map(stage => (
                <StyledMenuItem key={stage.value} className={stage.value === applicant?.jobStage?.toLowerCase() ? "selected-item" : ""}
                    onClick={e => chngStg(e, stage.value)}>{stage.label}</StyledMenuItem>
            ))}
        </Menu>}

    </>);
};